import { useCallback, useState, useRef, useEffect } from 'react';
import useUploadAlarmMutation from '../../hooks/useUploadAlarmMutation';
import { AlertType } from '../../../design-system/DSAlert';
import useAppContext from '../../../hooks/useAppContext';

const MAX_ALARM_DURATION_SECONDS = 40;

type UseAlarmUploadProps = {
  initialAlarmUrl?: string;
  initialAlarmName?: string;
  initialAlarmDuration?: number;
  showAlert: (alert: { message: string; alertType: AlertType }) => void;
  onUploadSuccess?: (url: string) => void;
  onRemove?: () => void;
};

type UseAlarmUploadReturn = {
  alarmUrl: string;
  alarmName: string;
  alarmDurationInSeconds: number | undefined;
  isLoadingAlarm: boolean;
  handleAlarmFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveAlarm: () => void;
};

const useAlarmUpload = ({
  initialAlarmUrl = '',
  initialAlarmName = '',
  initialAlarmDuration,
  showAlert,
  onUploadSuccess,
  onRemove,
}: UseAlarmUploadProps): UseAlarmUploadReturn => {
  const { isAdmin } = useAppContext();
  const [alarmUrl, setAlarmUrl] = useState<string>(initialAlarmUrl);
  const [alarmName, setAlarmName] = useState<string>(initialAlarmName);
  const [alarmDurationInSeconds, setAlarmDurationInSeconds] = useState<
    number | undefined
  >(initialAlarmDuration);

  const alarmAbortControllerRef = useRef<AbortController | null>(null);
  const { mutate: uploadAlarmMutation, isPending: isLoadingAlarm } =
    useUploadAlarmMutation();

  useEffect(() => {
    return () => {
      if (alarmAbortControllerRef.current) {
        alarmAbortControllerRef.current.abort();
      }
    };
  }, []);

  const handleAlarmFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validTypes = ['audio/mp3', 'audio/wave', 'audio/wav', 'audio/mpeg'];

      if (!validTypes.includes(file.type)) {
        showAlert({
          message: 'Invalid alarm file type. Please upload MP3 or WAV files.',
          alertType: AlertType.ERROR,
        });
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        alarmAbortControllerRef.current = new AbortController();
        uploadAlarmMutation(
          {
            file: reader.result as string,
            fileName: file.name,
            fileType: file.type,
          },
          {
            onSuccess: (response) => {
              if (response.status === 'approved' && response.url) {
                if (
                  response.duration &&
                  response.duration > MAX_ALARM_DURATION_SECONDS &&
                  !isAdmin
                ) {
                  showAlert({
                    message: `Alarm duration must be less than ${MAX_ALARM_DURATION_SECONDS} seconds. Your file is ${response.duration} seconds.`,
                    alertType: AlertType.ERROR,
                  });
                  event.target.value = '';
                  return;
                }
                setAlarmUrl(response.url);
                setAlarmName(file.name);
                setAlarmDurationInSeconds(response.duration);
                onUploadSuccess?.(response.url);
              } else {
                showAlert({
                  message: response.error || 'Failed to upload alarm',
                  alertType: AlertType.ERROR,
                });
                event.target.value = '';
              }
            },
            onError: () => {
              showAlert({
                message: 'Failed to upload alarm',
                alertType: AlertType.ERROR,
              });
              event.target.value = '';
            },
          },
        );
      };
      reader.readAsDataURL(file);
    },
    [showAlert, uploadAlarmMutation],
  );

  const handleRemoveAlarm = useCallback(() => {
    if (alarmAbortControllerRef.current) {
      alarmAbortControllerRef.current.abort();
    }
    setAlarmUrl('');
    setAlarmName('');
    setAlarmDurationInSeconds(undefined);
    onRemove?.();
  }, [onRemove]);

  return {
    alarmUrl,
    alarmName,
    alarmDurationInSeconds,
    isLoadingAlarm,
    handleAlarmFileChange,
    handleRemoveAlarm,
  };
};

export default useAlarmUpload;
